import { SyncOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import type { Event, EventProps } from 'react-big-calendar';

import type { FrequencyMode } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';
import { differenceInMinutes, format } from 'app/utils/dayjs';

import styles from './CalendarEvent.module.scss';
import { useSeriesHighlight } from './seriesHighlightContext';

export type TimeslotEventResource = {
  id: string;
  seriesId?: string;
  slotsLeft: number;
  totalBooked: number;
  hasStock: boolean;
  isRecurrent: boolean;
  isClosed: boolean;
  isTemporary?: boolean;
  frequency?: FrequencyMode;
  weekdays?: number[];
};

export type TimeslotEvent = Event & { resource: TimeslotEventResource };

const COMPACT_MAX_DURATION_MIN = 30;
const LARGE_MIN_DURATION_MIN = 120;

type Props = EventProps & {
  selected?: boolean;
};

export const CalendarEvent = ({ event, selected = false }: Props) => {
  const { start, end } = event;

  const {
    seriesId,
    slotsLeft = 0,
    totalBooked = 0,
    hasStock = true,
    isRecurrent = false,
    isClosed = false,
  } = (event.resource ?? {}) as Partial<TimeslotEventResource>;

  const { hoveredSeriesId, setHoveredSeriesId } = useSeriesHighlight();

  const isSeriesHighlighted = !!seriesId && seriesId === hoveredSeriesId;

  const handleMouseEnter = () => {
    if (seriesId) {
      setHoveredSeriesId(seriesId);
    }
  };

  const handleMouseLeave = () => {
    if (seriesId) {
      setHoveredSeriesId(null);
    }
  };

  const durationMin = start && end ? differenceInMinutes(end, start) : 0;
  const isCompact = durationMin > 0 && durationMin <= COMPACT_MAX_DURATION_MIN;
  const isLarge = durationMin >= LARGE_MIN_DURATION_MIN;

  const startLabel = start ? format(start, 'HH:mm') : '';
  const endLabel = end ? format(end, 'HH:mm') : '';

  const trailingLabel = isClosed ? 'Closed' : startLabel;
  const bottomLabel = isClosed ? 'Closed' : `${startLabel} - ${endLabel}`;

  const availabilities = (
    <div className={styles.availabilities}>
      <span className={styles.slotsLeft}>{slotsLeft} left</span>
      <span className={styles.booked}>· {totalBooked} booked</span>
    </div>
  );

  const sync = isRecurrent ? <SyncOutlined className={styles.sync} /> : null;

  return (
    <div
      className={classNames(styles.card, {
        [styles.hasStock]: !isClosed && hasStock,
        [styles.noStock]: !isClosed && !hasStock,
        [styles.closed]: isClosed,
        [styles.compact]: isCompact,
        [styles.large]: !isCompact && isLarge,
        [styles.selected]: selected,
        [styles.seriesHighlight]: isSeriesHighlighted,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isCompact ? (
        <div className={styles.header}>
          {availabilities}
          <div className={styles.hourRepeat}>
            <span className={styles.time}>{trailingLabel}</span>
            {sync}
          </div>
        </div>
      ) : (
        <>
          <div className={styles.header}>
            {availabilities}
            {sync}
          </div>
          <div className={styles.timeFrame}>{bottomLabel}</div>
        </>
      )}
    </div>
  );
};
